import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import Hls from 'hls.js';
import { mediaSessionArtwork } from '@/lib/api';
import { resolveTrackArtist } from '@/lib/artist';
import { setVideoSessionActive } from '@/lib/videoSession';
import { usePlayer } from '@/hooks/usePlayer';
import type { Track } from '@/types';

type VideoElWithWebkit = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
  webkitSupportsFullscreen?: boolean;
};

export type NowPlayingVideoHandle = {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  seek: (timeSec: number) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  isPlaying: () => boolean;
  /** iOS native video FS / standard element fullscreen (landscape rotate). */
  enterNativeFullscreen: () => Promise<boolean>;
  exitNativeFullscreen: () => void;
  isNativeFullscreen: () => boolean;
};

function publishVideoPosition(video: HTMLVideoElement, trackDuration?: number) {
  if (!('mediaSession' in navigator)) return;
  const fromEl = Number.isFinite(video.duration) ? video.duration : NaN;
  const duration =
    Number.isFinite(trackDuration) && (trackDuration as number) > 0
      ? (trackDuration as number)
      : Number.isFinite(fromEl) && fromEl > 0
        ? fromEl
        : NaN;
  if (!Number.isFinite(duration) || duration <= 0) return;
  const position = Math.min(Math.max(0, video.currentTime || 0), duration);
  try {
    navigator.mediaSession.setPositionState({
      duration,
      playbackRate: video.playbackRate || 1,
      position,
    });
  } catch {
    /* unsupported */
  }
}

type Props = {
  videoUrl: string;
  track: Track;
  /** Release cover for Media Session. */
  artwork?: string;
  /** Release title for Media Session. */
  album?: string;
  /** Release artist fallback (track artist wins when set). */
  artist?: string;
  /** Optional playlist neighbors for lock-screen next/prev. */
  onPrevious?: () => void;
  onNext?: () => void;
  canPrevious?: boolean;
  canNext?: boolean;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  onEnded?: () => void;
};

/**
 * Embedded HLS/native video for the now-playing artwork / mini-tray slot.
 * Owns Media Session like on-demand audio so playback survives drawer dismiss
 * and lock-screen / background (where the platform allows video audio).
 */
export const NowPlayingVideo = forwardRef<NowPlayingVideoHandle, Props>(
  function NowPlayingVideo(
    {
      videoUrl,
      track,
      artwork: artworkProp,
      album: albumProp,
      artist: artistProp,
      onPrevious,
      onNext,
      canPrevious = false,
      canNext = false,
      onTimeUpdate,
      onPlayingChange,
      onEnded,
    },
    ref,
  ) {
    const videoElRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const wantPlayingRef = useRef(true);
    const onPreviousRef = useRef(onPrevious);
    const onNextRef = useRef(onNext);
    onPreviousRef.current = onPrevious;
    onNextRef.current = onNext;
    const { artwork: playerArtwork } = usePlayer();
    const artwork = artworkProp?.trim() || playerArtwork;
    const sessionArtist = resolveTrackArtist(track.artist, artistProp);
    const sessionAlbum =
      albumProp?.trim() || track.albumName?.trim() || '';
    const [ready, setReady] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          wantPlayingRef.current = true;
          void videoElRef.current?.play();
        },
        pause: () => {
          wantPlayingRef.current = false;
          videoElRef.current?.pause();
        },
        toggle: () => {
          const v = videoElRef.current;
          if (!v) return;
          if (v.paused) {
            wantPlayingRef.current = true;
            void v.play();
          } else {
            wantPlayingRef.current = false;
            v.pause();
          }
        },
        seek: (timeSec: number) => {
          const v = videoElRef.current;
          if (!v || !Number.isFinite(timeSec)) return;
          v.currentTime = Math.max(0, timeSec);
          onTimeUpdate?.(v.currentTime, v.duration || 0);
          publishVideoPosition(v, track.duration);
        },
        getCurrentTime: () => videoElRef.current?.currentTime ?? 0,
        getDuration: () => videoElRef.current?.duration || 0,
        isPlaying: () =>
          Boolean(videoElRef.current && !videoElRef.current.paused),
        enterNativeFullscreen: async () => {
          const v = videoElRef.current as VideoElWithWebkit | null;
          if (!v) return false;
          wantPlayingRef.current = true;
          try {
            await v.play();
          } catch {
            /* may already be playing */
          }
          // iPhone: only the video element’s native presentation works.
          if (typeof v.webkitEnterFullscreen === 'function') {
            try {
              v.webkitEnterFullscreen();
              return true;
            } catch {
              /* fall through */
            }
          }
          try {
            if (v.requestFullscreen) {
              await v.requestFullscreen();
              return true;
            }
            const anyV = v as HTMLElement & {
              webkitRequestFullscreen?: () => void;
            };
            if (typeof anyV.webkitRequestFullscreen === 'function') {
              anyV.webkitRequestFullscreen();
              return true;
            }
          } catch {
            return false;
          }
          return false;
        },
        exitNativeFullscreen: () => {
          const v = videoElRef.current as VideoElWithWebkit | null;
          const doc = document as Document & {
            webkitFullscreenElement?: Element | null;
            webkitExitFullscreen?: () => void;
          };
          if (v?.webkitDisplayingFullscreen && v.webkitExitFullscreen) {
            try {
              v.webkitExitFullscreen();
            } catch {
              /* ignore */
            }
            return;
          }
          if (doc.fullscreenElement || doc.webkitFullscreenElement) {
            if (doc.exitFullscreen) void doc.exitFullscreen();
            else doc.webkitExitFullscreen?.();
          }
        },
        isNativeFullscreen: () => {
          const v = videoElRef.current as VideoElWithWebkit | null;
          if (v?.webkitDisplayingFullscreen) return true;
          const doc = document as Document & {
            webkitFullscreenElement?: Element | null;
          };
          const fs = doc.fullscreenElement ?? doc.webkitFullscreenElement;
          return Boolean(fs && v && (fs === v || v.contains(fs as Node)));
        },
      }),
      [onTimeUpdate, track.duration],
    );

    // Session stays active across playlist URL swaps — only clear on unmount.
    useEffect(() => {
      setVideoSessionActive(true);
      return () => setVideoSessionActive(false);
    }, []);

    useEffect(() => {
      const video = videoElRef.current;
      if (!video) return;

      setReady(false);
      wantPlayingRef.current = true;
      setVideoSessionActive(true);
      const absolute = videoUrl.startsWith('http')
        ? videoUrl
        : new URL(videoUrl, 'https://media.thisiszacharygray.com').href;

      if (Hls.isSupported()) {
        const hls = new Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 5,
        });
        hlsRef.current = hls;
        hls.loadSource(absolute);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setReady(true);
          void video.play();
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = absolute;
        setReady(true);
        void video.play();
      }

      return () => {
        hlsRef.current?.destroy();
        hlsRef.current = null;
        try {
          // Pause only — do not clear src/load. Clearing flashes a blank
          // (non-black) frame on iOS between playlist tracks; the next attach
          // replaces the source while the host/#000 stays painted.
          video.pause();
        } catch {
          /* ignore */
        }
        if ('mediaSession' in navigator) {
          navigator.mediaSession.setActionHandler('play', null);
          navigator.mediaSession.setActionHandler('pause', null);
          navigator.mediaSession.setActionHandler('previoustrack', null);
          navigator.mediaSession.setActionHandler('nexttrack', null);
          navigator.mediaSession.setActionHandler('seekto', null);
          navigator.mediaSession.setActionHandler('seekforward', null);
          navigator.mediaSession.setActionHandler('seekbackward', null);
        }
      };
    }, [videoUrl]);

    useEffect(() => {
      if (!('mediaSession' in navigator)) return;
      const video = videoElRef.current;
      setVideoSessionActive(true);

      navigator.mediaSession.metadata = new MediaMetadata({
        title: track.name,
        artist: sessionArtist,
        album: sessionAlbum,
        artwork: mediaSessionArtwork(artwork),
      });

      navigator.mediaSession.setActionHandler('play', () => {
        wantPlayingRef.current = true;
        void videoElRef.current?.play();
      });
      navigator.mediaSession.setActionHandler('pause', () => {
        wantPlayingRef.current = false;
        videoElRef.current?.pause();
      });

      // Playlist: next/prev track. Solo video: seek ± (same as before).
      const hasPlaylistNeighbors = canPrevious || canNext;
      if (hasPlaylistNeighbors) {
        navigator.mediaSession.setActionHandler('seekforward', null);
        navigator.mediaSession.setActionHandler('seekbackward', null);
        navigator.mediaSession.setActionHandler(
          'previoustrack',
          canPrevious
            ? () => {
                onPreviousRef.current?.();
              }
            : null,
        );
        navigator.mediaSession.setActionHandler(
          'nexttrack',
          canNext
            ? () => {
                onNextRef.current?.();
              }
            : null,
        );
      } else {
        navigator.mediaSession.setActionHandler('previoustrack', null);
        navigator.mediaSession.setActionHandler('nexttrack', null);
        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          const v = videoElRef.current;
          if (!v) return;
          v.currentTime = Math.max(0, v.currentTime - (details.seekOffset ?? 10));
          publishVideoPosition(v, track.duration);
        });
        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          const v = videoElRef.current;
          if (!v) return;
          const dur = Number.isFinite(v.duration) ? v.duration : track.duration || 0;
          v.currentTime = Math.min(dur, v.currentTime + (details.seekOffset ?? 10));
          publishVideoPosition(v, track.duration);
        });
      }

      navigator.mediaSession.setActionHandler('seekto', (details) => {
        const v = videoElRef.current;
        if (!v || details.seekTime == null) return;
        v.currentTime = details.seekTime;
        publishVideoPosition(v, track.duration);
      });

      if (video) publishVideoPosition(video, track.duration);

      return undefined;
    }, [
      videoUrl,
      track,
      artwork,
      sessionAlbum,
      sessionArtist,
      canPrevious,
      canNext,
    ]);

    useEffect(() => {
      const video = videoElRef.current;
      if (!video) return;

      const onTime = () => {
        onTimeUpdate?.(video.currentTime, video.duration || 0);
        publishVideoPosition(video, track.duration);
      };
      const onPlay = () => {
        wantPlayingRef.current = true;
        onPlayingChange?.(true);
        if ('mediaSession' in navigator) {
          try {
            navigator.mediaSession.playbackState = 'playing';
          } catch {
            /* unsupported */
          }
        }
        publishVideoPosition(video, track.duration);
      };
      const onPause = () => {
        // System / portal stalls: keep intent + Media Session “playing”, retry play
        // so lock-screen / background can reclaim the element.
        if (wantPlayingRef.current) {
          if ('mediaSession' in navigator) {
            try {
              navigator.mediaSession.playbackState = 'playing';
            } catch {
              /* unsupported */
            }
          }
          publishVideoPosition(video, track.duration);
          window.setTimeout(() => {
            if (!wantPlayingRef.current) return;
            const v = videoElRef.current;
            if (!v || !v.paused || v.ended) return;
            void v.play().catch(() => {
              /* may stay paused until wake */
            });
          }, 120);
          return;
        }
        onPlayingChange?.(false);
        if ('mediaSession' in navigator) {
          try {
            navigator.mediaSession.playbackState = 'paused';
          } catch {
            /* unsupported */
          }
        }
        publishVideoPosition(video, track.duration);
      };
      const onEnd = () => onEnded?.();
      const onMeta = () => {
        setReady(true);
        onTimeUpdate?.(video.currentTime, video.duration || 0);
        publishVideoPosition(video, track.duration);
      };

      video.addEventListener('timeupdate', onTime);
      video.addEventListener('play', onPlay);
      video.addEventListener('pause', onPause);
      video.addEventListener('ended', onEnd);
      video.addEventListener('loadedmetadata', onMeta);
      video.addEventListener('seeked', onTime);
      return () => {
        video.removeEventListener('timeupdate', onTime);
        video.removeEventListener('play', onPlay);
        video.removeEventListener('pause', onPause);
        video.removeEventListener('ended', onEnd);
        video.removeEventListener('loadedmetadata', onMeta);
        video.removeEventListener('seeked', onTime);
      };
    }, [onTimeUpdate, onPlayingChange, onEnded, videoUrl, track.duration]);

    useEffect(() => {
      const reassertSession = () => {
        const video = videoElRef.current;
        if (!video || !wantPlayingRef.current) return;
        setVideoSessionActive(true);
        if ('mediaSession' in navigator) {
          try {
            navigator.mediaSession.playbackState = 'playing';
          } catch {
            /* unsupported */
          }
        }
        publishVideoPosition(video, track.duration);
        if (video.paused || video.ended) {
          void video.play().catch(() => {
            /* platform may block until foreground */
          });
        }
      };

      const onVisibility = () => {
        // Hidden (lock screen): keep session + try to stay playing.
        // Visible: reclaim if the OS paused us.
        reassertSession();
      };
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pageshow', onVisibility);
      // Periodic position refresh while locked — iOS drops Media Session times.
      const tick = window.setInterval(() => {
        if (!wantPlayingRef.current) return;
        const video = videoElRef.current;
        if (!video) return;
        publishVideoPosition(video, track.duration);
      }, 5000);
      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pageshow', onVisibility);
        window.clearInterval(tick);
      };
    }, [track.duration, videoUrl]);

    return (
      <video
        ref={videoElRef}
        className={`now-playing-sheet-video${ready ? ' is-ready' : ''}`}
        playsInline
        autoPlay
        controlsList="nodownload noplaybackrate"
        // Keep AirPlay / remote routes available for lock-screen continuity.
        {...{
          'webkit-playsinline': 'true',
          'x-webkit-airplay': 'allow',
        }}
      />
    );
  },
);
